// Active Projects Calendar  (Tom Clancy 2026-08-05)
// -----------------------------------------------------------------
// Day / Week / Month calendar view of the Active Projects list.
// For each active job with Pre-Final and/or Final dates entered, we
// render a colored chip in every day cell that falls within its
// Pre-Final Start->End or Final Start->End window.
//
// Depends on: GET /api/wip/active (server/routes/active-wip.js)
// Sibling of: /pages/ActiveProjects.js (the date-entry page)

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, IconButton, Button, ToggleButtonGroup,
  ToggleButton, FormControl, InputLabel, Select, MenuItem, Alert,
  LinearProgress, Tooltip, Divider,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getActiveWip } from '../services/api';

// -----------------------------------------------------------------
// Constants + helpers
// -----------------------------------------------------------------
const STAGE_COLORS = {
  prefinal: { bg: '#FFE0B2', fg: '#8F4B00', border: '#F57C00' }, // amber
  final:    { bg: '#E1BEE7', fg: '#4A148C', border: '#6A1B9A' }, // purple
};

const MS_DAY = 24 * 60 * 60 * 1000;

const startOfDay = (d) => {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
};
const addDays = (d, n) => new Date(startOfDay(d).getTime() + n * MS_DAY);
const isoDay = (d) => {
  const x = startOfDay(d);
  return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}-${String(x.getDate()).padStart(2,'0')}`;
};
const parseISO = (s) => {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return startOfDay(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
};
const sameDay = (a, b) => a && b && isoDay(a) === isoDay(b);

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
// Business-day calendar (Tom Clancy 2026-08-05): ACS doesn't work Sat/Sun,
// so the week runs Monday-Friday and weekends never appear on the grid.
const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri']; // Mon-Fri only
const isBusinessDay = (d) => { const dow = d.getDay(); return dow >= 1 && dow <= 5; };

// Week starts Monday, ends Friday.
const startOfWeek  = (d) => {
  const x = startOfDay(d);
  const dow = x.getDay();
  // Mon=1 -> 0 back; Sun=0 -> jump forward to next Mon; Sat=6 -> forward 2 to Mon.
  const backToMonday = dow === 0 ? -6 : (1 - dow);
  return addDays(x, backToMonday);
};
const endOfWeek    = (d) => addDays(startOfWeek(d), 4); // Mon + 4 = Fri
const startOfMonth = (d) => { const x = startOfDay(d); x.setDate(1); return x; };
const endOfMonth   = (d) => { const x = startOfMonth(d); x.setMonth(x.getMonth() + 1); return addDays(x, -1); };

// -----------------------------------------------------------------
// Expand each project into per-stage windows.
// -----------------------------------------------------------------
function expandWindows(projects) {
  const out = [];
  for (const p of projects || []) {
    const ps = parseISO(p.prefinal_start);
    const pe = parseISO(p.prefinal_end);
    const fs = parseISO(p.final_start);
    const fe = parseISO(p.final_end);
    if (ps && pe && ps <= pe) out.push({ project: p, stage: 'prefinal', start: ps, end: pe });
    if (fs && fe && fs <= fe) out.push({ project: p, stage: 'final',    start: fs, end: fe });
  }
  return out;
}

// isoDay -> [{ project, stage }, ...]
// Skips weekends: if a stage window spans a weekend, only the Mon-Fri days
// inside it get chips. The window still "exists" over the weekend for the
// project record, but the calendar itself is business days only.
function bucketByDay(windows, rangeStart, rangeEnd) {
  const map = new Map();
  for (const w of windows) {
    const lo = w.start < rangeStart ? rangeStart : w.start;
    const hi = w.end   > rangeEnd   ? rangeEnd   : w.end;
    if (lo > hi) continue;
    for (let d = new Date(lo); d <= hi; d = addDays(d, 1)) {
      if (!isBusinessDay(d)) continue;
      const key = isoDay(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(w);
    }
  }
  return map;
}

// -----------------------------------------------------------------
// Project chip (used in month + week)
// -----------------------------------------------------------------
function ProjectChip({ w, compact = false, onClick }) {
  const c = STAGE_COLORS[w.stage];
  const label = compact
    ? (w.project.job_number || '—')
    : `${w.project.job_number} · ${w.project.name}`;
  return (
    <Tooltip title={
      <Box sx={{ fontSize: 12 }}>
        <div><strong>{w.project.job_number} — {w.project.name}</strong></div>
        <div>{w.stage === 'prefinal' ? 'Pre-Final' : 'Final'}</div>
        <div>PM: {w.project.pm || '—'} · Sales: {w.project.salesman || '—'}</div>
        <div>{w.project.discipline || '—'}</div>
      </Box>
    }>
      <Chip
        size="small"
        clickable
        onClick={(e) => { e.stopPropagation(); onClick(w.project); }}
        label={label}
        sx={{
          m: 0.25,
          maxWidth: '100%',
          justifyContent: 'flex-start',
          bgcolor: c.bg,
          color: c.fg,
          borderLeft: `3px solid ${c.border}`,
          borderRadius: 1,
          fontSize: 11,
          height: 'auto',
          '& .MuiChip-label': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            width: '100%',
            py: 0.25,
          },
        }}
      />
    </Tooltip>
  );
}

// -----------------------------------------------------------------
// Month view
// -----------------------------------------------------------------
function MonthView({ anchor, windows, onOpen }) {
  const first = startOfMonth(anchor);
  const gridStart = startOfWeek(first);
  const last = endOfMonth(anchor);
  const gridEnd = endOfWeek(last);

  // Business-day grid: keep only Mon-Fri cells across the full grid window.
  const daysInGrid = [];
  for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) {
    if (isBusinessDay(d)) daysInGrid.push(new Date(d));
  }
  const bucket = bucketByDay(windows, gridStart, gridEnd);
  const today = startOfDay(new Date());

  return (
    <Paper variant="outlined">
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', bgcolor: '#f5f5f5' }}>
        {DAY_NAMES.map(d => (
          <Box key={d} sx={{ p: 1, fontWeight: 600, fontSize: 12, textAlign: 'center' }}>{d}</Box>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {daysInGrid.map(d => {
          const key = isoDay(d);
          const items = bucket.get(key) || [];
          const preCount = items.filter(w => w.stage === 'prefinal').length;
          const finCount = items.filter(w => w.stage === 'final').length;
          const inMonth = d.getMonth() === first.getMonth();
          const isToday = sameDay(d, today);
          return (
            <Box key={key} sx={{
              borderTop: '1px solid #e0e0e0',
              borderLeft: '1px solid #e0e0e0',
              minHeight: 120,
              p: 0.5,
              opacity: inMonth ? 1 : 0.45,
              bgcolor: isToday ? '#FFFDE7' : 'white',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                <Typography variant="caption" fontWeight={isToday ? 700 : 500} sx={{ fontSize: 12 }}>
                  {d.getDate()}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {preCount > 0 && (
                  <Chip size="small" label={`${preCount} P`}
                    sx={{ height: 16, fontSize: 10, bgcolor: STAGE_COLORS.prefinal.bg, color: STAGE_COLORS.prefinal.fg }} />
                )}
                {finCount > 0 && (
                  <Chip size="small" label={`${finCount} F`}
                    sx={{ height: 16, fontSize: 10, bgcolor: STAGE_COLORS.final.bg, color: STAGE_COLORS.final.fg }} />
                )}
              </Stack>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {items.slice(0, 4).map((w, i) => (
                  <ProjectChip key={`${w.project.id}-${w.stage}-${i}`} w={w} compact onClick={onOpen} />
                ))}
                {items.length > 4 && (
                  <Typography variant="caption" sx={{ pl: 0.5, color: 'text.secondary', fontSize: 10 }}>
                    +{items.length - 4} more
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// -----------------------------------------------------------------
// Week view
// -----------------------------------------------------------------
function WeekView({ anchor, windows, onOpen }) {
  const start = startOfWeek(anchor); // Monday
  const days = Array.from({ length: 5 }, (_, i) => addDays(start, i)); // Mon-Fri
  const bucket = bucketByDay(windows, start, endOfWeek(anchor));
  const today = startOfDay(new Date());
  const dayLabel = (d) => DAY_NAMES[d.getDay() - 1]; // Mon=1 -> DAY_NAMES[0]
  return (
    <Paper variant="outlined">
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', bgcolor: '#f5f5f5' }}>
        {days.map(d => (
          <Box key={isoDay(d)} sx={{ p: 1, fontWeight: 600, fontSize: 12, textAlign: 'center' }}>
            {dayLabel(d)} {d.getMonth()+1}/{d.getDate()}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
        {days.map(d => {
          const key = isoDay(d);
          const items = bucket.get(key) || [];
          const isToday = sameDay(d, today);
          const preItems = items.filter(w => w.stage === 'prefinal');
          const finItems = items.filter(w => w.stage === 'final');
          return (
            <Box key={key} sx={{
              borderTop: '1px solid #e0e0e0',
              borderLeft: '1px solid #e0e0e0',
              minHeight: 320,
              p: 1,
              bgcolor: isToday ? '#FFFDE7' : 'white',
            }}>
              <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                <Chip size="small" label={`Pre-Final · ${preItems.length}`}
                  sx={{ bgcolor: STAGE_COLORS.prefinal.bg, color: STAGE_COLORS.prefinal.fg, fontSize: 10, height: 20 }} />
                <Chip size="small" label={`Final · ${finItems.length}`}
                  sx={{ bgcolor: STAGE_COLORS.final.bg, color: STAGE_COLORS.final.fg, fontSize: 10, height: 20 }} />
              </Stack>
              <Box>
                {preItems.map((w, i) => (
                  <ProjectChip key={`p-${w.project.id}-${i}`} w={w} onClick={onOpen} />
                ))}
                {finItems.map((w, i) => (
                  <ProjectChip key={`f-${w.project.id}-${i}`} w={w} onClick={onOpen} />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// -----------------------------------------------------------------
// Day view
// -----------------------------------------------------------------
function DayView({ anchor, windows, onOpen }) {
  const day = startOfDay(anchor);
  const isWeekend = !isBusinessDay(day);
  const bucket = bucketByDay(windows, day, day);
  const items = bucket.get(isoDay(day)) || [];
  const pre = items.filter(w => w.stage === 'prefinal');
  const fin = items.filter(w => w.stage === 'final');
  const isToday = sameDay(day, startOfDay(new Date()));
  const fullDayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][day.getDay()];
  if (isWeekend) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {fullDayName}, {MONTH_NAMES[day.getMonth()]} {day.getDate()}, {day.getFullYear()}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Weekend — not a scheduled work day.
        </Typography>
      </Paper>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: isToday ? '#FFFDE7' : 'white' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {fullDayName}, {MONTH_NAMES[day.getMonth()]} {day.getDate()}, {day.getFullYear()}
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STAGE_COLORS.prefinal.border }} />
            <Typography variant="subtitle1" fontWeight={600}>Pre-Final ({pre.length})</Typography>
          </Stack>
          {pre.length === 0 && <Typography variant="body2" color="text.secondary"><em>No projects in Pre-Final today.</em></Typography>}
          {pre.map((w, i) => (
            <Paper key={`p-${w.project.id}-${i}`} variant="outlined"
              sx={{ p: 1, mb: 1, borderLeft: `3px solid ${STAGE_COLORS.prefinal.border}`, cursor: 'pointer' }}
              onClick={() => onOpen(w.project)}
            >
              <Typography variant="body2" fontWeight={600}>{w.project.job_number} · {w.project.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                PM: {w.project.pm || '—'} · Sales: {w.project.salesman || '—'} · {w.project.discipline}
              </Typography>
            </Paper>
          ))}
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STAGE_COLORS.final.border }} />
            <Typography variant="subtitle1" fontWeight={600}>Final ({fin.length})</Typography>
          </Stack>
          {fin.length === 0 && <Typography variant="body2" color="text.secondary"><em>No projects in Final today.</em></Typography>}
          {fin.map((w, i) => (
            <Paper key={`f-${w.project.id}-${i}`} variant="outlined"
              sx={{ p: 1, mb: 1, borderLeft: `3px solid ${STAGE_COLORS.final.border}`, cursor: 'pointer' }}
              onClick={() => onOpen(w.project)}
            >
              <Typography variant="body2" fontWeight={600}>{w.project.job_number} · {w.project.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                PM: {w.project.pm || '—'} · Sales: {w.project.salesman || '—'} · {w.project.discipline}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
}

// -----------------------------------------------------------------
// Main
// -----------------------------------------------------------------
export default function ActiveCalendar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [anchor, setAnchor] = useState(startOfDay(new Date()));
  const [view, setView] = useState('month');
  const [pmFilter, setPmFilter] = useState('');
  const [salesFilter, setSalesFilter] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await getActiveWip();
      setProjects(data.projects || []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Role-aware default: PM users pre-filtered to their own name.
  useEffect(() => {
    if (!user || projects.length === 0) return;
    if (pmFilter) return;
    const isPM = user.role === 'PM' || (Array.isArray(user.roles) && user.roles.includes('PM'));
    if (!isPM) return;
    const myName = user.name || '';
    const match = projects.map(p => p.pm).filter(Boolean).find(n => n && myName && n.toLowerCase() === myName.toLowerCase());
    if (match) setPmFilter(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projects]);

  const pms = useMemo(() =>
    Array.from(new Set(projects.map(p => p.pm).filter(Boolean))).sort(), [projects]);
  const sales = useMemo(() =>
    Array.from(new Set(projects.map(p => p.salesman).filter(Boolean))).sort(), [projects]);
  const disciplines = useMemo(() =>
    Array.from(new Set(projects.map(p => p.discipline).filter(Boolean))).sort(), [projects]);

  const filtered = useMemo(() => {
    let out = projects;
    if (pmFilter) out = out.filter(p => p.pm === pmFilter);
    if (salesFilter) out = out.filter(p => p.salesman === salesFilter);
    if (disciplineFilter) out = out.filter(p => p.discipline === disciplineFilter);
    return out;
  }, [projects, pmFilter, salesFilter, disciplineFilter]);

  const windows = useMemo(() => expandWindows(filtered), [filtered]);

  // Range totals for the header
  const rangeTotals = useMemo(() => {
    let lo, hi, label;
    if (view === 'day')   { lo = anchor; hi = anchor; label = 'today'; }
    if (view === 'week')  { lo = startOfWeek(anchor); hi = endOfWeek(anchor); label = 'this week'; }
    if (view === 'month') { lo = startOfMonth(anchor); hi = endOfMonth(anchor); label = 'this month'; }
    const bucket = bucketByDay(windows, lo, hi);
    const jobsPre = new Set();
    const jobsFin = new Set();
    for (const arr of bucket.values()) for (const w of arr) {
      if (w.stage === 'prefinal') jobsPre.add(w.project.id);
      else jobsFin.add(w.project.id);
    }
    return { pre: jobsPre.size, fin: jobsFin.size, label };
  }, [windows, view, anchor]);

  const openProject = (p) => navigate(`/projects/${p.id}`);

  // Business-day navigation. In day view we skip weekends. If we already
  // land on Sat/Sun via 'Today' or a fresh load, stepping forward jumps to
  // Monday, backward to Friday.
  const nextBusinessDay = (d) => {
    let x = addDays(d, 1);
    while (!isBusinessDay(x)) x = addDays(x, 1);
    return x;
  };
  const prevBusinessDay = (d) => {
    let x = addDays(d, -1);
    while (!isBusinessDay(x)) x = addDays(x, -1);
    return x;
  };
  const step = (delta) => {
    if (view === 'day')   setAnchor(a => delta > 0 ? nextBusinessDay(a) : prevBusinessDay(a));
    if (view === 'week')  setAnchor(a => addDays(a, delta * 7));
    if (view === 'month') setAnchor(a => {
      const x = new Date(a); x.setMonth(x.getMonth() + delta); x.setDate(1);
      return x;
    });
  };
  const goToday = () => {
    let d = startOfDay(new Date());
    if (view === 'day' && !isBusinessDay(d)) {
      // Weekend + day-view: jump forward to Monday instead of showing empty.
      d = nextBusinessDay(d);
    }
    setAnchor(d);
  };

  const headerLabel = useMemo(() => {
    const fullDay = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][anchor.getDay()];
    if (view === 'day')   return `${fullDay}, ${MONTH_NAMES[anchor.getMonth()]} ${anchor.getDate()}, ${anchor.getFullYear()}`;
    if (view === 'week')  {
      const s = startOfWeek(anchor); const e = endOfWeek(anchor);
      return `${MONTH_NAMES[s.getMonth()]} ${s.getDate()} – ${MONTH_NAMES[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
    }
    return `${MONTH_NAMES[anchor.getMonth()]} ${anchor.getFullYear()}`;
  }, [anchor, view]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Active Projects Calendar</Typography>
        <Chip size="small" label={`Pre-Final: ${rangeTotals.pre} · Final: ${rangeTotals.fin} · ${rangeTotals.label}`} />
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton size="small" onClick={() => step(-1)}><PrevIcon /></IconButton>
            <Button size="small" startIcon={<TodayIcon />} onClick={goToday}>Today</Button>
            <IconButton size="small" onClick={() => step(1)}><NextIcon /></IconButton>
            <Typography variant="h6" sx={{ ml: 1, minWidth: 260 }}>{headerLabel}</Typography>
          </Stack>
          <Box sx={{ flex: 1 }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>PM</InputLabel>
            <Select label="PM" value={pmFilter} onChange={(e) => setPmFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {pms.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sales</InputLabel>
            <Select label="Sales" value={salesFilter} onChange={(e) => setSalesFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {sales.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Discipline</InputLabel>
            <Select label="Discipline" value={disciplineFilter} onChange={(e) => setDisciplineFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {disciplines.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {loading && <LinearProgress />}

      <Stack direction="row" spacing={2} sx={{ mb: 1, ml: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, bgcolor: STAGE_COLORS.prefinal.bg, borderLeft: `3px solid ${STAGE_COLORS.prefinal.border}` }} />
          <Typography variant="caption">Pre-Final</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 12, height: 12, bgcolor: STAGE_COLORS.final.bg, borderLeft: `3px solid ${STAGE_COLORS.final.border}` }} />
          <Typography variant="caption">Final</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          Click any block to open the project page.
        </Typography>
      </Stack>

      {view === 'day'   && <DayView   anchor={anchor} windows={windows} onOpen={openProject} />}
      {view === 'week'  && <WeekView  anchor={anchor} windows={windows} onOpen={openProject} />}
      {view === 'month' && <MonthView anchor={anchor} windows={windows} onOpen={openProject} />}

      {!loading && windows.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No dated jobs yet. Once PMs enter Pre-Final and Final dates on the{' '}
          <strong>Active Projects</strong> page, they will appear here automatically.
        </Alert>
      )}
    </Box>
  );
}
